import { DateValue } from "@mantine/dates"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { FilterChipData } from "../odoo/filter-chip"

export type DistributingFilters = {
  refCode?: string
  deliveryDate?: [DateValue, DateValue]
  receiverBusinessPartnerBusinessNameRefCode?: string
  receiverBusinessProfileNameRefCode?: string
  receiverBranchRefCode?: string
  salesCode?: string
  paymentTermDescription?: string
  deliveryNoteStatus?: string | null
  staffFirstNameEmail?: string
  pullSheetStatus?: string | null
  totalItemCount: [string | number | null, string | number | null]
  totalAmount: [string | number | null, string | number | null]
  discountAmount: [string | number | null, string | number | null]
  giveawayAmount: [string | number | null, string | number | null]
  amountToPay: [string | number | null, string | number | null]
  paidAmount: [string | number | null, string | number | null]
  searchQuery?: string
}

const initialFilter: DistributingFilters = {
  refCode: "",
  deliveryDate: [null, null],
  receiverBusinessPartnerBusinessNameRefCode: "",
  receiverBusinessProfileNameRefCode: "",
  receiverBranchRefCode: "",
  salesCode: "",
  paymentTermDescription: "",
  deliveryNoteStatus: null,
  staffFirstNameEmail: "",
  pullSheetStatus: null,
  totalItemCount: [null, null],
  totalAmount: [null, null],
  discountAmount: [null, null],
  giveawayAmount: [null, null],
  amountToPay: [null, null],
  paidAmount: [null, null],
  searchQuery: "",
}

type FilterLabelMap = { key: keyof DistributingFilters; label: string; format?: (val: any) => string | null }

function useFilterLabels(): FilterLabelMap[] {
  const { t } = useTranslation(["delivery", "common"])
  return [
    { key: "refCode", label: t("delivery:filter.deliveryNo") },
    { key: "salesCode", label: "SO#" },
    { key: "receiverBusinessPartnerBusinessNameRefCode", label: t("delivery:filter.buyer") },
    { key: "receiverBranchRefCode", label: t("delivery:filter.branch") },
    { key: "deliveryNoteStatus", label: t("delivery:filter.deliveryStatus") },
    { key: "pullSheetStatus", label: t("delivery:filter.loadingStatus") },
    { key: "staffFirstNameEmail", label: t("delivery:filter.responsible") },
    { key: "paymentTermDescription", label: t("delivery:filter.paymentTerm") },
    { key: "searchQuery", label: t("common:actions.search", { ns: "common" }) },
    {
      key: "deliveryDate", label: t("delivery:filter.deliveryDate"),
      format: (val: [DateValue, DateValue]) => {
        if (!val || (!val[0] && !val[1])) return null
        return `${val[0] || "..."} - ${val[1] || "..."}`
      },
    },
  ]
}

function getDisplayValue(val: any): string | null {
  if (val === null || val === undefined || val === "") return null
  if (Array.isArray(val)) {
    if (val.every((v) => v === null || v === undefined || v === "")) return null
    return val.filter(Boolean).join(" - ")
  }
  return String(val)
}

export function useDistributingFilters() {
  const [filters, setFilters] = useState<DistributingFilters>(structuredClone(initialFilter))
  const FILTER_LABELS = useFilterLabels()

  const setFilter = <K extends keyof DistributingFilters>(key: K, value: DistributingFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  const clearFilters = () => setFilters(structuredClone(initialFilter))

  const removeFilter = (key: keyof DistributingFilters) => {
    setFilters((prev) => ({ ...prev, [key]: (initialFilter as any)[key] }))
  }

  const activeFilterChips: FilterChipData[] = FILTER_LABELS.reduce<FilterChipData[]>((chips, item) => {
    const val = filters[item.key]
    const display = item.format ? item.format(val) : getDisplayValue(val)
    if (display) chips.push({ key: item.key, label: item.label, value: display, onRemove: () => removeFilter(item.key) })
    return chips
  }, [])

  const { searchQuery, ...apiFilters } = filters

  return { filters: apiFilters, setFilters, setFilter, clearFilters, removeFilter, activeFilterChips, searchQuery }
}
